import { Link } from "react-router-dom";
import { CalendarDays, Clock, MapPin, Users } from "lucide-react";
import { Sections, usePage } from "../../hooks/usePage";
import { useSite } from "../../context/SiteContext";
import { Loading, PageHero, QuoteBand, RichText, SectionHeading } from "../../components/site/Blocks";
import { Icon } from "../../lib/icons";
import { formatEventDate, venueLine } from "../../lib/format";

const EventFacts = () => {
  const { event } = useSite();
  const facts = [
    [CalendarDays, formatEventDate(event), event.date_confirmed ? null : "Date to be confirmed"],
    [Clock, `${event.start_time} – ${event.end_time} (Nassau time)`, null],
    [MapPin, venueLine(event), event.venue_confirmed ? null : "Venue to be confirmed"],
    [Users, event.attendance_format, null],
  ];
  return (
    <ul className="grid gap-4 sm:grid-cols-2" data-testid="event-facts">
      {facts.map(([Ico, text, note], i) => (
        <li key={i} className="flex gap-3 rounded-lg border border-slate-200 p-4">
          <Ico className="mt-0.5 shrink-0 text-burgundy" size={22} aria-hidden="true" />
          <span><span className="block font-semibold text-navy">{text}</span>{note && <span className="text-sm text-burgundy">{note}</span>}</span>
        </li>
      ))}
    </ul>
  );
};

const Overview = ({ data }) => (
  <>
    <PageHero eyebrow={data.eyebrow} title={data.heading} image={data.image_url} testid="event-hero">
      <Link to="/registration" className="btn-primary mt-8" data-testid="event-register-btn">Register Now</Link>
    </PageHero>
    <section className="py-16" data-testid="event-overview">
      <div className="container-site grid gap-12 lg:grid-cols-2">
        <RichText html={data.body} />
        <EventFacts />
      </div>
    </section>
  </>
);

const IconGrid = ({ items, testid }) => (
  <ul className="grid grid-cols-2 gap-6 md:grid-cols-3 lg:grid-cols-5" data-testid={testid}>
    {items.map((it) => (
      <li key={it.title} className="flex flex-col items-center rounded-lg border border-slate-200 bg-white p-5 text-center">
        <Icon name={it.icon} size={30} className="text-burgundy" />
        <span className="mt-3 font-semibold text-navy">{it.title}</span>
      </li>
    ))}
  </ul>
);

const Highlights = ({ data }) => <section className="bg-sand py-14"><div className="container-site"><IconGrid items={data.items} testid="event-highlights" /></div></section>;
const Audience = ({ data }) => (
  <section className="py-16"><div className="container-site"><SectionHeading title={data.heading} className="mb-8" /><IconGrid items={data.items} testid="event-audience" /></div></section>
);
const Agenda = ({ data }) => (
  <section className="bg-paleblue py-16" data-testid="event-agenda">
    <div className="container-site max-w-4xl">
      <SectionHeading title={data.heading} />
      {data.items?.length ? (
        <ol className="mt-6 divide-y divide-slate-300">
          {data.items.map((a, i) => <li key={i} className="flex gap-6 py-4"><span className="w-24 font-semibold text-burgundy">{a.time}</span><span><b className="text-navy">{a.title}</b><br />{a.description}</span></li>)}
        </ol>
      ) : <p className="mt-4 text-lg text-[#4A5568]">{data.note}</p>}
    </div>
  </section>
);

const RENDER = { overview: Overview, highlights: Highlights, audience: Audience, agenda: Agenda, quote: QuoteBand };

export default function EventPage() {
  const { page, loading } = usePage("event");
  if (loading) return <Loading />;
  return <div data-testid="event-page"><Sections page={page} render={RENDER} /></div>;
}
